import { Dispatch, memo, SetStateAction, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { AgendaAction } from "@/modules/patients/agenda"
import { AgendaTable } from "@/modules/patients/agenda/agenda-table"
import { AGENDA_TYPE } from "@/modules/patients/agenda/types-filter"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { chTimeWindowFragment$key } from "@/modules/patients/fragments/ch/__generated__/chTimeWindowFragment.graphql"
import { chTimeWindowFragment } from "@/modules/patients/fragments/ch/chTimeWindowFragment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { AppointmentAgenda, handleAgendaAndAppointment } from "@/modules/patients/libs/categorize-date-ranges"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  AppointmentEdges,
  ChAlternativeTriageEdges,
  ChExternalAssistancesEdges,
  DeAlternativeTriageEdges,
  TimeWindowsType,
  usePatientAgenda,
} from "@/modules/patients/services/usePatientAgenda"
import { useDrawerStore } from "@/stores/useDrawerStore"

type AgendaContainerProps = {
  types: AGENDA_TYPE[]
  patient: PatientFragment$data
  selectedAgendaRowId: string | null
  setSelectedAgendaRowId: Dispatch<SetStateAction<string | null>>
  onRowClick: (agenda: AppointmentAgenda, mode: AgendaAction) => void
}

const getFilteredAgendaData = (
  types: AGENDA_TYPE[],
  timeWindows: ReadonlyArray<TimeWindowsType>,
  appointments: AppointmentEdges,
  alternativeTriages: DeAlternativeTriageEdges | ChAlternativeTriageEdges,
  externalAssistances: ChExternalAssistancesEdges,
) => {
  const allSelected = types.length === 0
  return {
    timeWindows: allSelected || types.includes("TIME_WINDOW") ? timeWindows : [],
    appointments: allSelected || types.includes("TELECONSULTATION") ? appointments : null,
    alternativeTriages: allSelected || types.includes("ALTERNATIVE_TRIAGE") ? alternativeTriages : null,
    externalAssistances: allSelected || types.includes("EXTERNAL_ASSISTANCE") ? externalAssistances : null,
  }
}

const AgendaContainerContent = (props: AgendaContainerProps) => {
  const { patient, selectedAgendaRowId, setSelectedAgendaRowId, types, onRowClick } = props
  const { t, i18n } = useTranslation()
  const isEditMode = useEditMode()
  const countryCode = useCountryParam()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const { agendaData } = usePatientAgenda(countryCode, patient.id, i18n.language)
  const timeWindows = useFragment<chTimeWindowFragment$key>(
    chTimeWindowFragment,
    agendaData?.__typename === "ch_Patient" ? agendaData?.timeWindows : null,
  )

  const categorized = useMemo(() => {
    if (!agendaData) return null

    const { appointments, __typename } = agendaData
    const alternativeTriages =
      __typename === "ch_Patient" ? agendaData.ch_alternativeTriages : agendaData.de_alternativeTriages
    const externalAssistances = __typename === "ch_Patient" ? agendaData.externalAssistances : null

    const categorizeFor = (selectedTypes: AGENDA_TYPE[]) =>
      handleAgendaAndAppointment(
        getFilteredAgendaData(selectedTypes, timeWindows ?? [], appointments, alternativeTriages, externalAssistances),
        patient,
      )

    // `all` ignores the type filter so the open drawer is closed only on a real delete, not on filtering.
    return { filtered: categorizeFor(types), all: categorizeFor([]) }
  }, [agendaData, types, timeWindows, patient])

  const categorizedAgendas = categorized?.filtered ?? null

  const selectedRowStillExists =
    selectedAgendaRowId === null ||
    !categorized ||
    [...categorized.all.planned, ...categorized.all.ongoing, ...categorized.all.past].some(
      (agenda) => agenda.id === selectedAgendaRowId,
    )

  // Close the open drawer when the row it was opened for (e.g. a time window) is deleted while open.
  useEffect(() => {
    if (selectedAgendaRowId && !selectedRowStillExists) {
      closeDrawer()
      setSelectedAgendaRowId(null)
    }
  }, [selectedAgendaRowId, selectedRowStillExists, closeDrawer, setSelectedAgendaRowId])

  if (!categorizedAgendas) return null

  return (
    <div className="flex flex-col px-8 pb-8">
      <AgendaTable
        agendaEntryStatus="PLANNED"
        title={t("patient.agenda.planned")}
        isEditMode={isEditMode}
        handleRowClick={onRowClick}
        data={categorizedAgendas.planned}
        selectedAgendaRowId={selectedAgendaRowId}
      />
      <AgendaTable
        agendaEntryStatus="ONGOING"
        title={t("patient.agenda.ongoing")}
        isEditMode={isEditMode}
        handleRowClick={onRowClick}
        data={categorizedAgendas.ongoing}
        selectedAgendaRowId={selectedAgendaRowId}
      />
      <AgendaTable
        agendaEntryStatus="PAST"
        isEditMode={isEditMode}
        handleRowClick={onRowClick}
        data={categorizedAgendas.past}
        title={t("patient.agenda.past")}
        isPast={true}
        selectedAgendaRowId={selectedAgendaRowId}
      />
    </div>
  )
}

export const AgendaContainer = memo(AgendaContainerContent)
